<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";
import TimeAgo from "@/components/TimeAgo.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { usePresenceStore } from "@/stores/PresenceStore";

useBreadcrumbs().setBreadcrumb("User Management");

const presenceStore = usePresenceStore();
const users = ref([]);
const roles = ref([]);
const searchQuery = ref("");
const loading = ref(false);
const resetRequests = ref({});
const createForm = ref({
  name: "",
  email: "",
  password: "",
  confirmation: "",
  roles: [],
  active: true,
  saving: false,
  message: "",
});

const ensureResetRequest = (userId) => {
  if (!resetRequests.value[userId]) {
    resetRequests.value[userId] = {
      saving: false,
      message: "",
    };
  }
  return resetRequests.value[userId];
};

const roleNames = computed(() =>
  roles.value.map((role) => role.name).filter(Boolean),
);

const presenceByUserId = computed(() => new Map(
  presenceStore.entries.map((entry) => [entry.id, entry]),
));

const statusRank = {
  online: 0,
  idle: 1,
  offline: 2,
};

const toggleCreateRole = (roleName) => {
  const current = createForm.value.roles;
  createForm.value.roles = current.includes(roleName)
    ? current.filter((role) => role !== roleName)
    : [...current, roleName];
};

const resetCreateForm = () => {
  createForm.value.name = "";
  createForm.value.email = "";
  createForm.value.password = "";
  createForm.value.confirmation = "";
  createForm.value.roles = [];
  createForm.value.active = true;
};

const usersWithPresence = computed(() => users.value.map((user) => {
  const presence = presenceByUserId.value.get(user.id);
  return {
    ...user,
    presence: presence || {
      status: "offline",
      last_seen_at: null,
      current_title: null,
      current_path: null,
      active_sessions: 0,
    },
  };
}));

const filteredUsers = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const visibleUsers = !query
    ? usersWithPresence.value
    : usersWithPresence.value.filter((user) => {
    const roleMatch = (user.roles || []).some((role) =>
      role.toLowerCase().includes(query),
    );
    return (
      (user.name || "").toLowerCase().includes(query)
      || (user.email || "").toLowerCase().includes(query)
      || roleMatch
    );
  });

  return [...visibleUsers].sort((left, right) => {
    const leftRank = statusRank[left.presence?.status] ?? 9;
    const rightRank = statusRank[right.presence?.status] ?? 9;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return (left.name || "").localeCompare(right.name || "");
  });
});

const loadUsers = async () => {
  loading.value = true;
  try {
    const [usersResponse, rolesResponse] = await Promise.all([
      apiClient.get("/api/super/users"),
      apiClient.get("/api/role"),
    ]);
    users.value = usersResponse.data || [];
    roles.value = rolesResponse.data || [];
    await presenceStore.load();
  } finally {
    loading.value = false;
  }
};

const presenceDotClass = (status) => {
  if (status === "online") {
    return "bg-emerald-500";
  }
  if (status === "idle") {
    return "bg-amber-400";
  }
  return "bg-slate-300 dark:bg-slate-600";
};

const presenceBadgeClass = (status) => {
  if (status === "online") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
  }
  if (status === "idle") {
    return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
  }
  return "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600";
};

const presenceContext = (presence) => {
  if (presence?.current_title) {
    return presence.current_title;
  }
  if (presence?.current_path) {
    return presence.current_path;
  }
  return "No recent page data";
};

const presenceSubtitle = (presence) => {
  if (presence?.status === "online") {
    return "Active in app now";
  }
  if (presence?.status === "idle") {
    return "Connected but recently idle";
  }
  return presence?.last_seen_at ? "Offline" : "No recent presence";
};

const toggleActive = async (user) => {
  const nextValue = !user.active;
  user.active = nextValue;
  try {
    await apiClient.put(`/api/super/users/${user.id}/active`, {
      active: nextValue,
    });
  } catch {
    user.active = !nextValue;
  }
};

const toggleRole = async (user, roleName) => {
  const hasRole = (user.roles || []).includes(roleName);
  const nextRoles = hasRole
    ? user.roles.filter((role) => role !== roleName)
    : [...(user.roles || []), roleName];
  user.roles = nextRoles;
  try {
    const response = await apiClient.put(
      `/api/super/users/${user.id}/roles`,
      { roles: nextRoles },
    );
    user.roles = response.data?.roles || nextRoles;
  } catch {
    await loadUsers();
  }
};

const sendPasswordResetEmail = async (user) => {
  const request = ensureResetRequest(user.id);
  request.message = "";
  request.saving = true;
  try {
    const response = await apiClient.post(
      `/api/super/users/${user.id}/password-reset`,
      {},
      { suppressErrorAlert: true },
    );
    request.message = response.data?.message || "Password reset email sent.";
  } catch (error) {
    request.message =
      error.response?.data?.message ||
      "The password reset email could not be sent.";
  } finally {
    request.saving = false;
  }
};

const createUser = async () => {
  createForm.value.message = "";
  const name = createForm.value.name.trim();
  const email = createForm.value.email.trim();
  if (!name) {
    createForm.value.message = "Name is required.";
    return;
  }
  if (!email) {
    createForm.value.message = "Email is required.";
    return;
  }
  if (createForm.value.password.length < 8) {
    createForm.value.message = "Password must be at least 8 characters.";
    return;
  }
  if (createForm.value.password !== createForm.value.confirmation) {
    createForm.value.message = "Passwords do not match.";
    return;
  }
  createForm.value.saving = true;
  try {
    await apiClient.post("/api/super/users", {
      name,
      email,
      password: createForm.value.password,
      password_confirmation: createForm.value.confirmation,
      roles: createForm.value.roles,
      active: createForm.value.active,
    });
    createForm.value.message = "User created successfully.";
    resetCreateForm();
    await loadUsers();
  } catch {
    createForm.value.message = "User creation failed.";
  } finally {
    createForm.value.saving = false;
  }
};

onMounted(loadUsers);
</script>

<template>
  <div class="space-y-6">
    <div>
      <p class="text-xs uppercase tracking-[0.2em] text-slate-400">Super User</p>
      <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">User Management</h1>
      <p class="mt-2 text-sm text-slate-500">
        Send secure password reset links, update roles, and toggle access for team members.
      </p>
    </div>

    <div class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Create user</h2>
          <p class="text-xs text-slate-500 dark:text-slate-300">
            Add a new teammate and set their initial access.
          </p>
        </div>
        <button
          type="button"
          class="btn accent h-9 px-4"
          :disabled="createForm.saving"
          @click="createUser"
        >
          {{ createForm.saving ? "Creating..." : "Create User" }}
        </button>
      </div>
      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <input
          v-model="createForm.name"
          type="text"
          placeholder="Full name"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
        />
        <input
          v-model="createForm.email"
          type="email"
          placeholder="Email address"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
        />
        <input
          v-model="createForm.password"
          type="password"
          placeholder="Temporary password"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
        />
        <input
          v-model="createForm.confirmation"
          type="password"
          placeholder="Confirm password"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
        />
      </div>
      <div class="mt-4 flex flex-wrap items-center gap-3">
        <div class="flex flex-wrap gap-2">
          <span class="text-xs uppercase tracking-wide text-slate-400">Roles</span>
          <label
            v-for="role in roleNames"
            :key="`create-${role}`"
            class="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
          >
            <input
              type="checkbox"
              class="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              :checked="createForm.roles.includes(role)"
              @change="() => toggleCreateRole(role)"
            />
            <span>{{ role }}</span>
          </label>
        </div>
        <label class="ml-auto inline-flex items-center gap-2 text-sm">
          <input
            v-model="createForm.active"
            type="checkbox"
            class="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span class="text-slate-600 dark:text-slate-200">Active</span>
        </label>
      </div>
      <p
        v-if="createForm.message"
        class="mt-3 text-xs text-slate-500 dark:text-slate-300"
      >
        {{ createForm.message }}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search by name, email, or role"
        class="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
      />
      <button
        type="button"
        class="btn accent h-9 px-4"
        @click="loadUsers"
      >
        Refresh
      </button>
      <div class="ml-auto flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
        <span class="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
          <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
          {{ presenceStore.summary.online }} online
        </span>
        <span class="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
          <span class="h-2 w-2 rounded-full bg-amber-400"></span>
          {{ presenceStore.summary.idle }} idle
        </span>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div
        v-if="loading"
        class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300"
      >
        Loading users...
      </div>
      <div v-else-if="!filteredUsers.length" class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300">
        No users found.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm text-slate-700 dark:text-slate-200">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-700/40 dark:text-slate-300">
            <tr>
              <th class="px-6 py-3 text-left font-semibold">User</th>
              <th class="px-6 py-3 text-left font-semibold">Roles</th>
              <th class="px-6 py-3 text-left font-semibold">Active</th>
              <th class="px-6 py-3 text-left font-semibold">Password reset</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr v-for="user in filteredUsers" :key="user.id" class="align-top">
              <td class="px-6 py-4">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="h-2.5 w-2.5 rounded-full" :class="presenceDotClass(user.presence?.status)"></span>
                  <div class="font-semibold text-slate-900 dark:text-slate-100">
                    {{ user.name }}
                  </div>
                  <span
                    class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset"
                    :class="presenceBadgeClass(user.presence?.status)"
                  >
                    {{ user.presence?.status ?? "offline" }}
                  </span>
                </div>
                <div class="text-xs text-slate-500 dark:text-slate-400">
                  {{ user.email }}
                </div>
                <router-link
                  :to="{ name: 'user-authentication-audit', params: { id: user.id } }"
                  class="mt-2 inline-flex text-xs font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300"
                >
                  Audit sign-ins &amp; password resets
                </router-link>
                <div class="mt-2 text-xs text-slate-500 dark:text-slate-300">
                  {{ presenceSubtitle(user.presence) }}
                </div>
                <div class="mt-1 text-xs text-slate-400">
                  {{ presenceContext(user.presence) }}
                </div>
                <div v-if="user.presence?.last_seen_at" class="mt-1 flex items-center gap-1 text-xs text-slate-400">
                  <span>Seen</span>
                  <TimeAgo :data="user.presence.last_seen_at" />
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-2">
                  <label
                    v-for="role in roleNames"
                    :key="`${user.id}-${role}`"
                    class="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  >
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                      :checked="(user.roles || []).includes(role)"
                      @change="() => toggleRole(user, role)"
                    />
                    <span>{{ role }}</span>
                  </label>
                </div>
              </td>
              <td class="px-6 py-4">
                <label class="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    class="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    :checked="user.active"
                    @change="() => toggleActive(user)"
                  />
                  <span class="text-slate-600 dark:text-slate-200">
                    {{ user.active ? "Active" : "Disabled" }}
                  </span>
                </label>
              </td>
              <td class="px-6 py-4">
                <button
                  type="button"
                  class="text-sm font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300"
                  :disabled="ensureResetRequest(user.id).saving"
                  @click="sendPasswordResetEmail(user)"
                >
                  {{ ensureResetRequest(user.id).saving ? "Sending…" : "Send reset email" }}
                </button>
                <p
                  v-if="ensureResetRequest(user.id).message"
                  class="mt-2 max-w-xs text-xs text-slate-500 dark:text-slate-300"
                  aria-live="polite"
                >
                  {{ ensureResetRequest(user.id).message }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
